/**
 * Gamification Dashboard Page
 * Central hub for streaks, daily missions, peer challenges, and AI personas
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import StreakWidget from '../../components/StreakWidget';
import DailyMissions from '../../components/DailyMissions';
import PeerChallenge from '../../components/PeerChallenge';
import { Button } from '../../components/ui/Button';
import { Target, Users, Sparkles, Trophy, Flame, Crown, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PrestigeModal from '../../components/PrestigeModal';
import { PrestigeBadgeList } from '../../components/PrestigeBadge';
import { prestigeService } from '../../services/prestigeService';

export default function GamificationDashboard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('missions'); // missions, challenges, personas, prestige
  const [showPrestigeModal, setShowPrestigeModal] = useState(false);
  const [prestigeBadges, setPrestigeBadges] = useState([]);

  useEffect(() => {
    if (user) {
      loadPrestigeBadges();
    }
  }, [user]);

  const loadPrestigeBadges = async () => {
    try {
      const badges = await prestigeService.getPrestigeBadges(user.id);
      setPrestigeBadges(badges);
    } catch (err) {
      console.error('Failed to load prestige badges:', err);
    }
  };

  const handlePrestigeComplete = () => {
    // Reload badges and profile
    loadPrestigeBadges();
    // Optionally reload profile from AuthContext
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 sm:pb-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Achievements</h1>
          <p className="text-gray-600">Track your progress, compete with peers, and level up your skills</p>
        </div>

        {/* Streak Widget (always visible) */}
        <div className="mb-6">
          <StreakWidget />
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-gray-200 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('missions')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'missions'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Target className="w-4 h-4" />
            Daily Missions
          </button>
          <button
            onClick={() => setActiveTab('challenges')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'challenges'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="w-4 h-4" />
            Peer Challenges
          </button>
          <button
            onClick={() => setActiveTab('personas')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'personas'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            AI Personas
          </button>
          <button
            onClick={() => setActiveTab('prestige')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'prestige'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Crown className="w-4 h-4" />
            Prestige
            {profile?.prestige_level > 0 && (
              <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">
                {profile.prestige_level}
              </span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {activeTab === 'missions' && <DailyMissions />}
          {activeTab === 'challenges' && <PeerChallenge />}
          {activeTab === 'personas' && <PersonasTab />}
          {activeTab === 'prestige' && (
            <PrestigeTab
              profile={profile}
              badges={prestigeBadges}
              onOpenModal={() => setShowPrestigeModal(true)}
            />
          )}
        </div>

        {/* Quick Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            icon={Flame}
            label="Current Streak"
            value="0"
            color="orange"
          />
          <StatCard
            icon={Target}
            label="Missions Completed"
            value="0"
            color="blue"
          />
          <StatCard
            icon={Trophy}
            label="Challenges Won"
            value="0"
            color="yellow"
          />
          <StatCard
            icon={Sparkles}
            label="Personas Mastered"
            value="0"
            color="purple"
          />
        </div>
      </div>{/* Prestige Modal */}
      <PrestigeModal
        isOpen={showPrestigeModal}
        onClose={() => setShowPrestigeModal(false)}
        onPrestige={handlePrestigeComplete}
        userId={user?.id}
      />
    </div>
  );
}

function PrestigeTab({ profile, badges, onOpenModal }) {
  const navigate = useNavigate();
  const canPrestige = profile?.can_prestige || false;
  const prestigeLevel = profile?.prestige_level || 0;
  const currentLevel = profile?.level || 1;
  const lifetimeXp = profile?.lifetime_xp || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <Crown className="w-16 h-16 mx-auto text-purple-500 mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Prestige System</h3>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Reach Level 50 to prestige, reset your level, and unlock exclusive elite drills and badges
        </p>
      </div>

      {/* Current Prestige Status */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-800">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Prestige</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {prestigeLevel > 0 ? `P${prestigeLevel}` : 'None'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Level</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">{currentLevel}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Lifetime XP</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {lifetimeXp.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Prestige Action */}
      {canPrestige ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <TrendingUp className="text-yellow-600 dark:text-yellow-400 mt-1 flex-shrink-0" size={32} />
            <div className="flex-1">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                🎉 You Can Prestige Now!
              </h4>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You've reached Level 50! Prestige to reset your level, earn an exclusive badge, and unlock elite drills.
              </p>
              <Button
                onClick={onOpenModal}
                className="bg-gradient-to-r from-purple-600 to-indigo-600"
              >
                <Crown className="mr-2" size={18} />
                Prestige Now
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Progress to Level 50
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-2">
            <div
              className="bg-gradient-to-r from-purple-600 to-indigo-600 h-4 rounded-full transition-all"
              style={{ width: `${Math.min((currentLevel / 50) * 100, 100)}%` }}
            ></div>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Level {currentLevel} / 50
          </div>
        </div>
      )}

      {/* Earned Badges */}
      {badges.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Earned Prestige Badges
          </h4>
          <PrestigeBadgeList badges={badges} />
        </div>
      )}

      {/* Benefits */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Prestige Benefits
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <Trophy className="text-purple-500 mb-3" size={24} />
            <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Exclusive Badges</h5>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Earn Bronze, Silver, and Gold prestige badges displayed everywhere
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <Sparkles className="text-purple-500 mb-3" size={24} />
            <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Elite Drills</h5>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Unlock 5 new elite drills per prestige level
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <TrendingUp className="text-purple-500 mb-3" size={24} />
            <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Lifetime Progress</h5>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your lifetime XP is never reset, showing your true mastery
            </p>
          </div>
        </div>
      </div>

      {/* View Elite Drills */}
      {prestigeLevel > 0 && (
        <div className="text-center">
          <Button
            onClick={() => navigate('/app/prestige-drills')}
            variant="outline"
          >
            View My Elite Drills
          </Button>
        </div>
      )}
    </div>
  );
}

function PersonasTab() {
  return (
    <div className="space-y-4">
      <div className="text-center py-12">
        <Sparkles className="w-16 h-16 mx-auto text-purple-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">AI Persona Library</h3>
        <p className="text-gray-600 mb-6">
          Practice with realistic AI personas representing different buyer types
        </p>
        <Button onClick={() => window.location.href = '/app/ai-roleplay'}>
          Start Roleplay
        </Button>
      </div>

      {/* Placeholder for persona cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PersonaCard
          name="Patricia Chen"
          title="CFO"
          archetype="Numbers-Driven"
          difficulty={1.3}
          unlocked={true}
        />
        <PersonaCard
          name="Mike Torres"
          title="Small Business Owner"
          archetype="Budget-Conscious"
          difficulty={0.8}
          unlocked={true}
        />
        <PersonaCard
          name="Dr. Sarah Kim"
          title="VP of Technology"
          archetype="Detail-Oriented"
          difficulty={1.5}
          unlocked={false}
          premium={true}
        />
        <PersonaCard
          name="James Richardson"
          title="Sales Director"
          archetype="Results-Driven"
          difficulty={1.2}
          unlocked={false}
        />
      </div>
    </div>
  );
}

function PersonaCard({ name, title, archetype, difficulty, unlocked, premium }) {
  return (
    <div className={`border-2 rounded-lg p-4 ${unlocked ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold text-gray-900">{name}</h4>
          <p className="text-sm text-gray-600">{title}</p>
        </div>
        {premium && (
          <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded font-medium">
            Premium
          </span>
        )}
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Archetype:</span>
          <span className="font-medium text-gray-900">{archetype}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Difficulty:</span>
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`w-2 h-4 rounded ${
                  i < Math.round(difficulty * 2)
                    ? 'bg-orange-500'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {unlocked ? (
        <Button size="sm" variant="default" className="w-full mt-3">
          Practice Now
        </Button>
      ) : (
        <Button size="sm" variant="outline" className="w-full mt-3" disabled>
          Locked
        </Button>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    orange: 'from-orange-500 to-red-500',
    blue: 'from-blue-500 to-cyan-500',
    yellow: 'from-yellow-500 to-orange-500',
    purple: 'from-purple-500 to-pink-500'
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[color]} rounded-lg p-4 text-white`}>
      <Icon className="w-8 h-8 mb-2 opacity-80" />
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm opacity-90">{label}</div>
    </div>
  );
}
